import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

interface FormStepperProps {
  steps: { label: string }[];
  currentStep: number;
  visitedSteps: Set<number>;
  onStepClick?: (step: number) => void;
}

export function FormStepper({ steps, currentStep, visitedSteps, onStepClick }: FormStepperProps) {
  return (
    <nav aria-label="Etapas do formulário" className="mb-6">
      <div className="flex items-center justify-center gap-1">
        {steps.map((step, i) => {
          const isCompleted = visitedSteps.has(i) && i < currentStep;
          const isCurrent = i === currentStep;
          const isClickable = visitedSteps.has(i) && !isCurrent;

          return (
            <div key={i} className="flex items-center">
              {i > 0 && (
                <div
                  className={cn(
                    'h-0.5 w-6 sm:w-10 transition-colors duration-300',
                    i <= currentStep ? 'bg-primary' : 'bg-muted',
                  )}
                />
              )}
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(i)}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Etapa ${i + 1}: ${step.label}`}
                className={cn(
                  'flex flex-col items-center gap-1 group',
                  isClickable && 'cursor-pointer',
                  !isClickable && !isCurrent && 'cursor-default',
                )}
              >
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full border-2 text-xs font-semibold transition-all duration-300',
                    isCompleted && 'border-primary bg-primary text-primary-foreground',
                    isCurrent && 'border-primary bg-background text-primary shadow-sm',
                    !isCompleted && !isCurrent && 'border-muted bg-background text-muted-foreground',
                    isClickable && 'group-hover:border-primary/70 group-hover:text-primary',
                  )}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span
                  className={cn(
                    'hidden text-[11px] font-medium sm:block',
                    isCurrent ? 'text-primary' : 'text-muted-foreground',
                    isClickable && 'group-hover:text-primary',
                  )}
                >
                  {step.label}
                </span>
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-center text-sm text-muted-foreground sm:hidden">
        Etapa {currentStep + 1} de {steps.length} — {steps[currentStep].label}
      </p>
    </nav>
  );
}
